/**
 * API client — thin wrappers over fetch/EventSource.
 * All SSE consumption is handled by useAsk hook; these helpers cover REST calls.
 */

import type { VoiceConfig } from "./lib/serverStt";

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "";

// ---------------------------------------------------------------------------
// Session
// ---------------------------------------------------------------------------

export async function newSession(): Promise<string> {
  const resp = await fetch(`${BASE_URL}/session/new`);
  if (!resp.ok) throw new Error(`newSession failed: ${resp.status}`);
  const data = await resp.json();
  return data.session_id as string;
}

export async function deleteSession(sessionId: string): Promise<void> {
  await fetch(`${BASE_URL}/session/${sessionId}`, { method: "DELETE" });
}

// ---------------------------------------------------------------------------
// TTS
// ---------------------------------------------------------------------------

export async function fetchTTS(text: string): Promise<ArrayBuffer> {
  const resp = await fetch(`${BASE_URL}/tts`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ text }),
  });
  if (!resp.ok) {
    if (resp.status === 429) throw new Error("You're sending messages too quickly — please wait a moment and try again.");
    if (resp.status === 422) throw new Error("That message couldn't be sent. Please rephrase your question and try again.");
    throw new Error("Something went wrong. Please try again.");
  }
  return resp.arrayBuffer();
}

// ---------------------------------------------------------------------------
// Voice (decision 0015)
// ---------------------------------------------------------------------------

/** Which speech engine the backend wants clients to use; null when it can't say
 *  (an older backend without the route, maintenance mode, or a network error),
 *  which the caller treats as the browser engine. */
export async function fetchVoiceConfig(): Promise<VoiceConfig | null> {
  try {
    const resp = await fetch(`${BASE_URL}/voice/config`);
    if (!resp.ok) return null;
    const data = (await resp.json()) as Partial<VoiceConfig>;
    if (data.engine !== "browser" && data.engine !== "server") return null;
    return {
      engine: data.engine,
      server_available: data.server_available === true,
      max_seconds: typeof data.max_seconds === "number" ? data.max_seconds : 15,
      max_bytes: typeof data.max_bytes === "number" ? data.max_bytes : 524288,
    };
  } catch {
    return null;
  }
}
